import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { sortNewsByPublished } from "../../utils/newsSort";
import {
  getSourceKey,
  getSourceMapValue,
} from "../../utils/sourceModel";
import FocusPagerPage, {
  DEFAULT_INITIAL_BATCH_SIZE,
  FOCUS_RENDER_RADIUS,
  getFocusPagerItemKey,
} from "./FocusPagerPage";
import "./FocusPager.css";
import {
  dispatchFocusPagerPosition,
} from "../../utils/focusPagerProgress";

/*
|-----------------------------------------------------------------------------
| Android Focus Pager — Clean Transform-Based Horizontal Paging
|-----------------------------------------------------------------------------
|
| Principle: Structurally identical to iOS, but horizontal movement uses
| CSS transform instead of native scroll-snap. This avoids Chrome Android's
| nested-scroll conflict while keeping vertical scroll 100% native.
|
| - Each page has overflow-y: auto — the ONLY vertical scroll owner.
| - No pointer capture. Touch events on viewport detect horizontal swipe,
|   vertical touches pass through to the page's native scroller.
| - No inner scroll wrapper. The page element IS the scroll container.
| - No touch-action on children. Only the viewport has touch-action: pan-y
|   to tell Chrome "allow vertical scroll, I'll handle horizontal".
|
*/

const AXIS_LOCK_DISTANCE = 10;
const HORIZONTAL_AXIS_RATIO = 1.3;
const SWIPE_MIN_DISTANCE = 50;
const SWIPE_DISTANCE_RATIO = 0.14;
const SWIPE_MIN_VELOCITY = 0.4;
const SWIPE_MIN_VELOCITY_DISTANCE = 16;
const TRACK_TRANSITION_MS = 240;
const CLICK_SUPPRESSION_MS = 320;

const clamp = (value, minimum, maximum) =>
  Math.min(maximum, Math.max(minimum, value));

const getInitialActiveIndex = (sources, initialSource) => {
  const index = sources.indexOf(initialSource);
  return index >= 0 ? index : 0;
};

const getVisualIndexForSource = (source, orderedSources) => {
  const orderedIndex = orderedSources.indexOf(source);
  if (orderedIndex < 0) return 0;
  return orderedSources.length > 1 ? orderedIndex + 1 : orderedIndex;
};

const FocusPagerAndroid = ({
  sources = [],
  focusNewsBySource = {},
  favicons = {},
  onMarkAsRead,
  onNewsOpen,
  onSourceChange,
  initialSource,
  scrollToTopSignal,
  onActivePageAtTopChange,
  savedLinks,
  onToggleSaved,
  onShare,
  onView,
  onLoadMore,
  onEnsureInitial,
  hasMoreForSource,
  isLoadingMoreSource,
  hasCheckedInitialForSource,
  hasFailedInitialForSource,
  onRetryInitial,
  initialBatchSize = DEFAULT_INITIAL_BATCH_SIZE,
}) => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const progressRafRef = useRef(null);
  const progressAnimationRafRef = useRef(null);
  const gestureRef = useRef(null);
  const settleTimerRef = useRef(null);
  const activeIndexRef = useRef(
    getInitialActiveIndex(sources, initialSource)
  );
  const visualIndexRef = useRef(0);
  const pendingVisualIndexRef = useRef(null);
  const internalPageChangeRef = useRef(false);
  const isAnimatingRef = useRef(false);
  const suppressClickUntilRef = useRef(0);

  const orderedSources = useMemo(
    () => [...sources].reverse(),
    [sources]
  );

  const loopedSources = useMemo(() => {
    if (orderedSources.length <= 1) {
      return orderedSources;
    }
    return [
      orderedSources[orderedSources.length - 1],
      ...orderedSources,
      orderedSources[0],
    ];
  }, [orderedSources]);

  const initialActiveIndex = getInitialActiveIndex(sources, initialSource);
  const initialActiveSource =
    sources[initialActiveIndex] || initialSource || sources[0];

  const [activeIndex, setActiveIndex] = useState(initialActiveIndex);
  const [visualIndex, setVisualIndex] = useState(() =>
    getVisualIndexForSource(initialActiveSource, orderedSources)
  );
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 0 : window.innerWidth || 0
  );
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  activeIndexRef.current = activeIndex;
  visualIndexRef.current = visualIndex;
  isAnimatingRef.current = isAnimating;

  const activeSource =
    sources[activeIndex] || initialSource || sources[0];
  const activeVisualIndex = getVisualIndexForSource(
    activeSource,
    orderedSources
  );

  const emitRenderedPagerPosition = useCallback(() => {
    const container = containerRef.current;
    const track = trackRef.current;

    if (!container || !track) return;

    const width = container.getBoundingClientRect().width;
    if (width <= 0) return;

    const transform = window.getComputedStyle(track).transform;
    let translateX = 0;

    if (transform && transform !== "none") {
      const match = transform.match(/^matrix\(([^)]+)\)$/);
      const matrix3d = transform.match(/^matrix3d\(([^)]+)\)$/);

      if (matrix3d) {
        const values = matrix3d[1].split(",").map(Number);
        translateX = Number(values[12]) || 0;
      } else if (match) {
        const values = match[1].split(",").map(Number);
        translateX = Number(values[4]) || 0;
      }
    }

    dispatchFocusPagerPosition(
      -translateX / width,
      loopedSources
    );
  }, [loopedSources]);

  const scheduleRenderedPagerPosition = useCallback(() => {
    if (typeof requestAnimationFrame === "undefined") {
      emitRenderedPagerPosition();
      return;
    }

    if (progressRafRef.current !== null) {
      cancelAnimationFrame(progressRafRef.current);
    }

    progressRafRef.current = requestAnimationFrame(() => {
      progressRafRef.current = null;
      emitRenderedPagerPosition();
    });
  }, [emitRenderedPagerPosition]);

  const newsBySource = useMemo(() => {
    const grouped = new Map();
    const seenBySource = new Map();
    const sourceByKey = new Map();

    sources.forEach((source) => {
      grouped.set(source, []);
      seenBySource.set(source, new Set());
      sourceByKey.set(getSourceKey(source), source);
    });

    const pushUnique = (item, index) => {
      const canonicalSource = sourceByKey.get(
        getSourceKey(item?.source)
      );
      if (!canonicalSource) return;

      const key = getFocusPagerItemKey(item, index);
      const seen = seenBySource.get(canonicalSource);
      if (seen.has(key)) return;

      seen.add(key);
      grouped.get(canonicalSource).push(item);
    };

    Object.values(focusNewsBySource).forEach((items) => {
      if (!Array.isArray(items)) return;
      items.forEach(pushUnique);
    });

    grouped.forEach((items, source) => {
      grouped.set(source, sortNewsByPublished(items));
    });

    return grouped;
  }, [focusNewsBySource, sources]);

  const clearSettleTimer = useCallback(() => {
    if (
      settleTimerRef.current !== null &&
      typeof window !== "undefined"
    ) {
      window.clearTimeout(settleTimerRef.current);
    }
    settleTimerRef.current = null;
  }, []);

  /*
  |---------------------------------------------------------------------------
  | Viewport width measurement
  |---------------------------------------------------------------------------
  */
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const updateWidth = () => {
      const rect = container.getBoundingClientRect();
      if (rect.width > 0) {
        setViewportWidth(rect.width);
      }
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const shouldRenderPage = useCallback(
    (index) => {
      if (index < 0 || sources.length <= 1) return true;
      const directDistance = Math.abs(index - activeIndex);
      const loopDistance = sources.length - directDistance;
      return Math.min(directDistance, loopDistance) <= FOCUS_RENDER_RADIUS;
    },
    [activeIndex, sources.length]
  );

  const commitVisualIndex = useCallback(
    (targetVisualIndex) => {
      const source = loopedSources[targetVisualIndex];
      const nextActiveIndex = sources.indexOf(source);

      let normalizedVisualIndex = targetVisualIndex;
      if (orderedSources.length > 1) {
        if (targetVisualIndex === 0) {
          normalizedVisualIndex = orderedSources.length;
        } else if (targetVisualIndex === loopedSources.length - 1) {
          normalizedVisualIndex = 1;
        }
      }

      pendingVisualIndexRef.current = null;
      visualIndexRef.current = normalizedVisualIndex;
      isAnimatingRef.current = false;

      setIsAnimating(false);
      setIsDragging(false);
      setDragOffset(0);
      setVisualIndex(normalizedVisualIndex);

      if (nextActiveIndex < 0 || nextActiveIndex === activeIndexRef.current) {
        return;
      }

      activeIndexRef.current = nextActiveIndex;
      setActiveIndex(nextActiveIndex);
      onActivePageAtTopChange?.(true);

      internalPageChangeRef.current = true;
      onSourceChange?.(sources[nextActiveIndex]);
    },
    [
      loopedSources,
      onActivePageAtTopChange,
      onSourceChange,
      orderedSources.length,
      sources,
    ]
  );

  const finishTrackTransition = useCallback(() => {
    clearSettleTimer();
    const pending = pendingVisualIndexRef.current;

    if (pending === null) {
      isAnimatingRef.current = false;
      setIsAnimating(false);
      setIsDragging(false);
      setDragOffset(0);
      return;
    }

    commitVisualIndex(pending);
  }, [clearSettleTimer, commitVisualIndex]);

  const ensureTargetSourceInitial = useCallback(
    (targetVisualIndex) => {
      const targetSource = loopedSources[targetVisualIndex];
      if (!targetSource) return;

      const targetNews = newsBySource.get(targetSource) || [];
      const targetBatchCount = getSourceMapValue(
        focusNewsBySource,
        targetSource,
        []
      ).length;

      const hasEnoughItems = targetBatchCount >= initialBatchSize;
      const hasChecked = Boolean(
        hasCheckedInitialForSource?.(targetSource)
      );
      const isLoading = Boolean(
        isLoadingMoreSource?.(targetSource)
      );
      const hasFailed = Boolean(
        hasFailedInitialForSource?.(targetSource)
      );

      if (hasEnoughItems || hasChecked || isLoading || hasFailed) return;

      onEnsureInitial?.(targetSource, targetNews);
    },
    [
      focusNewsBySource,
      hasCheckedInitialForSource,
      hasFailedInitialForSource,
      initialBatchSize,
      isLoadingMoreSource,
      loopedSources,
      newsBySource,
      onEnsureInitial,
    ]
  );

  const beginTrackTransition = useCallback(
    (targetVisualIndex = null) => {
      clearSettleTimer();
      pendingVisualIndexRef.current = targetVisualIndex;

      if (targetVisualIndex !== null) {
        ensureTargetSourceInitial(targetVisualIndex);
      }

      isAnimatingRef.current = true;
      setIsDragging(false);
      setIsAnimating(true);
      setDragOffset(0);

      if (targetVisualIndex !== null) {
        visualIndexRef.current = targetVisualIndex;
        setVisualIndex(targetVisualIndex);
      }

      if (typeof window !== "undefined") {
        settleTimerRef.current = window.setTimeout(
          finishTrackTransition,
          TRACK_TRANSITION_MS + 60
        );
      }
    },
    [clearSettleTimer, ensureTargetSourceInitial, finishTrackTransition]
  );

  /*
  |---------------------------------------------------------------------------
  | Physical Focus pager -> ticker progress
  |---------------------------------------------------------------------------
  |
  | During direct drag, emit after each rendered transform update. During the
  | 240ms CSS settle, sample the track's real computed transform every frame.
  | The ticker therefore follows the page that is actually painted on screen,
  | not a second guessed animation timeline.
  |---------------------------------------------------------------------------
  */
  useLayoutEffect(() => {
    scheduleRenderedPagerPosition();
  }, [
    dragOffset,
    scheduleRenderedPagerPosition,
    viewportWidth,
    visualIndex,
  ]);

  useEffect(() => {
    if (!isAnimating || typeof requestAnimationFrame === "undefined") {
      return undefined;
    }

    let cancelled = false;

    const sample = () => {
      if (cancelled) return;

      emitRenderedPagerPosition();
      progressAnimationRafRef.current = requestAnimationFrame(sample);
    };

    progressAnimationRafRef.current = requestAnimationFrame(sample);

    return () => {
      cancelled = true;

      if (progressAnimationRafRef.current !== null) {
        cancelAnimationFrame(progressAnimationRafRef.current);
        progressAnimationRafRef.current = null;
      }
    };
  }, [emitRenderedPagerPosition, isAnimating]);

  /*
  |---------------------------------------------------------------------------
  | External source changes (filter-bar taps)
  |---------------------------------------------------------------------------
  */
  useLayoutEffect(() => {
    const nextActiveIndex = sources.indexOf(initialSource);
    if (nextActiveIndex < 0) return;

    if (internalPageChangeRef.current) {
      if (nextActiveIndex === activeIndexRef.current) {
        internalPageChangeRef.current = false;
      }
      return;
    }

    clearSettleTimer();

    const nextVisualIndex = getVisualIndexForSource(
      initialSource,
      orderedSources
    );

    gestureRef.current = null;
    pendingVisualIndexRef.current = null;
    activeIndexRef.current = nextActiveIndex;
    visualIndexRef.current = nextVisualIndex;

    setActiveIndex(nextActiveIndex);
    setVisualIndex(nextVisualIndex);
    isAnimatingRef.current = false;
    setDragOffset(0);
    setIsDragging(false);
    setIsAnimating(false);
    onActivePageAtTopChange?.(true);
  }, [
    clearSettleTimer,
    initialSource,
    onActivePageAtTopChange,
    orderedSources,
    sources,
  ]);

  useEffect(() => {
    return () => clearSettleTimer();
  }, [clearSettleTimer]);

  /*
  |---------------------------------------------------------------------------
  | Touch handlers — NO pointer capture, NO preventDefault on vertical
  |---------------------------------------------------------------------------
  |
  | We use Touch Events (not Pointer Events) because:
  | 1. No automatic capture — touches naturally bubble to scrollers
  | 2. Chrome Android handles touch-action correctly with touch events
  | 3. We can still intercept horizontal intent before it becomes scroll
  |
  | The viewport has touch-action: pan-y, which tells Chrome:
  | "Let vertical scroll pass through; I'll handle horizontal in JS."
  |
  | When vertical intent is detected, we stop tracking immediately and
  | let the native scroller take over. No capture means no stealing.
  |
  */
  const handleTouchStart = useCallback(
    (event) => {
      if (sources.length <= 1) return;

      const touch = event.touches[0];
      gestureRef.current = {
        identifier: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        currentX: touch.clientX,
        currentY: touch.clientY,
        startTime: performance.now(),
        axis: null,
      };

      setDragOffset(0);
      setIsDragging(false);
    },
    [sources.length]
  );

  const handleTouchMove = useCallback(
    (event) => {
      const gesture = gestureRef.current;
      if (!gesture) return;

      const touch = Array.from(event.touches).find(
        (t) => t.identifier === gesture.identifier
      );
      if (!touch) return;

      gesture.currentX = touch.clientX;
      gesture.currentY = touch.clientY;

      const dx = gesture.currentX - gesture.startX;
      const dy = gesture.currentY - gesture.startY;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      if (gesture.axis === null) {
        if (Math.max(absX, absY) < AXIS_LOCK_DISTANCE) {
          return;
        }

        if (absX > absY * HORIZONTAL_AXIS_RATIO) {
          gesture.axis = "horizontal";
          setIsDragging(true);
        } else {
          // Vertical intent — abandon gesture immediately.
          // No capture was set, so native scroll takes over naturally.
          gestureRef.current = null;
          setIsDragging(false);
          setDragOffset(0);
          return;
        }
      }

      if (gesture.axis !== "horizontal") {
        return;
      }

      // Horizontal drag — prevent default to stop native horizontal scroll
      if (event.cancelable) {
        event.preventDefault();
      }

      const maxDrag = Math.max(viewportWidth, 1) * 1.02;
      setDragOffset(clamp(dx, -maxDrag, maxDrag));
    },
    [viewportWidth]
  );

  const settleGesture = useCallback(
    (clientX, clientY, cancelled = false) => {
      const gesture = gestureRef.current;
      gestureRef.current = null;

      if (!gesture || cancelled) {
        setIsDragging(false);
        setDragOffset(0);
        return;
      }

      if (gesture.axis !== "horizontal") {
        setIsDragging(false);
        setDragOffset(0);
        return;
      }

      const dx = clientX - gesture.startX;
      const elapsed = Math.max(performance.now() - gesture.startTime, 1);
      const distance = Math.abs(dx);
      const velocity = distance / elapsed;
      const width = Math.max(viewportWidth, 1);

      const distanceThreshold = Math.max(
        SWIPE_MIN_DISTANCE,
        width * SWIPE_DISTANCE_RATIO
      );

      const shouldCommit =
        distance >= distanceThreshold ||
        (distance >= SWIPE_MIN_VELOCITY_DISTANCE &&
          velocity >= SWIPE_MIN_VELOCITY);

      if (distance >= AXIS_LOCK_DISTANCE) {
        suppressClickUntilRef.current = Date.now() + CLICK_SUPPRESSION_MS;
      }

      if (!shouldCommit) {
        beginTrackTransition(null);
        return;
      }

      const visualDirection = dx < 0 ? 1 : -1;
      const targetVisualIndex = clamp(
        visualIndexRef.current + visualDirection,
        0,
        loopedSources.length - 1
      );

      beginTrackTransition(targetVisualIndex);
    },
    [beginTrackTransition, loopedSources.length, viewportWidth]
  );

  const handleTouchEnd = useCallback(
    (event) => {
      const gesture = gestureRef.current;
      if (!gesture) return;

      const touch = Array.from(event.changedTouches).find(
        (t) => t.identifier === gesture.identifier
      );
      if (!touch) return;

      settleGesture(touch.clientX, touch.clientY, false);
    },
    [settleGesture]
  );

  const handleTouchCancel = useCallback(
    (event) => {
      const gesture = gestureRef.current;
      if (!gesture) return;

      const touch = Array.from(event.changedTouches).find(
        (t) => t.identifier === gesture.identifier
      );
      if (!touch) return;

      settleGesture(touch.clientX, touch.clientY, true);
    },
    [settleGesture]
  );

  const handleClickCapture = useCallback((event) => {
    if (Date.now() >= suppressClickUntilRef.current) return;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleTransitionEnd = useCallback(
    (event) => {
      if (event.target !== event.currentTarget) return;
      if (event.propertyName !== "transform") return;
      finishTrackTransition();
    },
    [finishTrackTransition]
  );

  if (sources.length === 0) return null;

  const trackTranslateX = -(visualIndex * viewportWidth) + dragOffset;

  return (
    <div className="focus-pager focus-pager--android">
      <div
        className="focus-pager__container"
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        onClickCapture={handleClickCapture}
      >
        <div
          ref={trackRef}
          className={`focus-pager__track${
            isDragging ? " focus-pager__track--dragging" : ""
          }${isAnimating ? " focus-pager__track--animating" : ""}`}
          style={{
            transform: `translate3d(${trackTranslateX}px, 0, 0)`,
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {loopedSources.map((source, pageVisualIndex) => {
            const index = sources.indexOf(source);
            const sourceNews = newsBySource.get(source) || [];
            const focusBatchCount = getSourceMapValue(
              focusNewsBySource,
              source,
              []
            ).length;

            const isPageRendered = shouldRenderPage(index);
            const isActivePage =
              index === activeIndex && pageVisualIndex === activeVisualIndex;

            const canonicalVisualIndex = getVisualIndexForSource(
              source,
              orderedSources
            );

            const isCanonicalPage =
              pageVisualIndex === canonicalVisualIndex;

            const isSourceLoading = Boolean(
              isLoadingMoreSource?.(source)
            );

            const hasSourceCheckedInitial = Boolean(
              hasCheckedInitialForSource?.(source)
            );

            const hasSourceInitialError = Boolean(
              hasFailedInitialForSource?.(source)
            );

            if (!isPageRendered) {
              return (
                <div
                  key={`${source}-${pageVisualIndex}`}
                  className="focus-pager__page focus-pager__page--placeholder"
                  aria-hidden="true"
                />
              );
            }

            return (
              <FocusPagerPage
                key={`${source}-${pageVisualIndex}`}
                className={`focus-pager__page${
                  isActivePage ? "" : " focus-pager__page--inactive"
                }`}
                sourceKey={getSourceKey(source)}
                sourceNews={sourceNews}
                isPageRendered={isPageRendered}
                isActivePage={isActivePage}
                favicons={favicons}
                onMarkAsRead={onMarkAsRead}
                onNewsOpen={onNewsOpen}
                scrollToTopSignal={
                  isActivePage ? scrollToTopSignal : 0
                }
                onAtTopChange={
                  isActivePage ? onActivePageAtTopChange : undefined
                }
                savedLinks={savedLinks}
                onToggleSaved={onToggleSaved}
                onShare={onShare}
                onView={onView}
                onLoadMore={
                  isActivePage
                    ? () => onLoadMore?.(source, sourceNews)
                    : undefined
                }
                onEnsureInitial={
                  isCanonicalPage
                    ? () => onEnsureInitial?.(source, sourceNews)
                    : undefined
                }
                hasMore={
                  isActivePage ? hasMoreForSource?.(source) : false
                }
                isLoadingMore={isSourceLoading}
                hasCheckedInitial={hasSourceCheckedInitial}
                hasInitialError={hasSourceInitialError}
                onRetryInitial={
                  isCanonicalPage
                    ? () => onRetryInitial?.(source, sourceNews)
                    : undefined
                }
                initialBatchSize={initialBatchSize}
                focusBatchCount={focusBatchCount}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FocusPagerAndroid;